import React, { useState } from 'react';
import styled from 'styled-components';

const ReportsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CardContent = styled.div``;

const FiltersCard = styled(Card)`
  grid-column: span 3;
`;

const FilterForm = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  font-size: 0.75rem;
  color: #64748b;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const FilterButton = styled.button`
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 15px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #2563eb;
  }
`;

const ChartCard = styled(Card)`
  grid-column: span ${props => props.span || 1};
`;

const ChartContainer = styled.div`
  height: 300px;
  background-color: #f8fafc;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.$hasLegend ? '15px' : '0'};
`;

const ChartLegend = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  color: #64748b;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background-color: ${props => props.color};
`;

const StatsCard = styled(Card)`
  grid-column: span 1;
`;

const StatsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
`;

const StatChange = styled.div`
  font-size: 0.75rem;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  display: flex;
  align-items: center;
  gap: 3px;
`;

const Reports = () => {
  const [selectedZone, setSelectedZone] = useState('all');
  const [selectedMetric, setSelectedMetric] = useState('moisture');
  const [dateRange, setDateRange] = useState('7d');
  
  // Mock data - in a real app, this would come from API calls
  const zones = [
    { id: 'all', name: 'All Zones' },
    { id: 'zone-1', name: 'North Field' },
    { id: 'zone-2', name: 'South Field' }
  ];
  
  const metrics = [
    { id: 'moisture', name: 'Soil Moisture' },
    { id: 'temperature', name: 'Temperature' },
    { id: 'nutrients', name: 'Nutrient Levels' },
    { id: 'yield', name: 'Yield Prediction' }
  ];
  
  const dateRanges = [
    { id: '7d', name: 'Last 7 Days' },
    { id: '30d', name: 'Last 30 Days' },
    { id: '90d', name: 'Last 90 Days' },
    { id: 'ytd', name: 'Year to Date' }
  ];
  
  // Stats data would be calculated based on the selected filters
  const stats = {
    average: '68%',
    change: '+3%',
    positive: true,
    min: '62%',
    max: '75%',
    optimal: '70%'
  };
  
  return (
    <ReportsContainer>
      <FiltersCard>
        <CardHeader>
          <CardTitle>Report Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <FilterForm>
            <FilterGroup>
              <FilterLabel>Zone</FilterLabel>
              <FilterSelect 
                value={selectedZone} 
                onChange={(e) => setSelectedZone(e.target.value)}
              >
                {zones.map(zone => (
                  <option key={zone.id} value={zone.id}>{zone.name}</option>
                ))}
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Metric</FilterLabel>
              <FilterSelect 
                value={selectedMetric} 
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                {metrics.map(metric => (
                  <option key={metric.id} value={metric.id}>{metric.name}</option>
                ))}
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Date Range</FilterLabel>
              <FilterSelect 
                value={dateRange} 
                onChange={(e) => setDateRange(e.target.value)}
              >
                {dateRanges.map(range => (
                  <option key={range.id} value={range.id}>{range.name}</option>
                ))}
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Custom Date Range</FilterLabel>
              <div style={{ display: 'flex', gap: '10px' }}>
                <FilterInput type="date" placeholder="Start Date" />
                <FilterInput type="date" placeholder="End Date" />
              </div>
            </FilterGroup>
            
            <FilterButton>Apply Filters</FilterButton>
          </FilterForm>
        </CardContent>
      </FiltersCard>
      
      <ChartCard span={2}>
        <CardHeader>
          <CardTitle>Soil Moisture Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer $hasLegend={true}>
            {/* In a real app, this would be a Chart.js or similar chart */}
            <div>Soil Moisture Chart Placeholder</div>
          </ChartContainer>
          <ChartLegend>
            <LegendItem>
              <LegendColor color="#3b82f6" />
              <span>Actual</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#10b981" />
              <span>Optimal</span>
            </LegendItem>
            <LegendItem>
              <LegendColor color="#f59e0b" />
              <span>Minimum</span>
            </LegendItem>
          </ChartLegend>
        </CardContent>
      </ChartCard>
      
      <StatsCard>
        <CardHeader>
          <CardTitle>Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <StatsList>
            <StatItem>
              <StatLabel>Average Soil Moisture</StatLabel>
              <StatValue>{stats.average}</StatValue>
              <StatChange $positive={stats.positive}>
                {stats.positive ? '↑' : '↓'} {stats.change} from previous period
              </StatChange>
            </StatItem>
            <StatItem>
              <StatLabel>Minimum</StatLabel>
              <StatValue>{stats.min}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Maximum</StatLabel>
              <StatValue>{stats.max}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Optimal Level</StatLabel>
              <StatValue>{stats.optimal}</StatValue>
            </StatItem>
          </StatsList>
        </CardContent>
      </StatsCard>
      
      <ChartCard span={3}>
        <CardHeader>
          <CardTitle>Comparison by Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer>
            {/* In a real app, this would be a Chart.js or similar chart */}
            <div>Zone Comparison Chart Placeholder</div>
          </ChartContainer>
        </CardContent>
      </ChartCard>
    </ReportsContainer>
  );
};

export default Reports;
